/**
 * AUTOCONSTRAIN inference engine (Faz 6.6, OCS infer_constraints parity).
 *
 * Scans draftable entities pairwise and proposes Coincident / Collinear /
 * Parallel / Perpendicular / Tangent / Concentric / Horizontal / Vertical
 * (+ optional Equal) constraints. Coincident uses OCS's dedicated tight
 * epsilon (1e-6); the remaining distance tests honor
 * settings.distanceTolerance (default 0.05) and angle tests
 * settings.angleToleranceDeg (default 1.0). Self-pairs and duplicates of
 * existing constraints are filtered out; bulged polyline segments and
 * single-object rectangles (derived corners, not writable endpoints) are
 * excluded like OCS filters bulge > 1e-9.
 */

import { useAppStore, type Entity } from '../../state/store';
import type { Pt3 } from '../commands/geometryMath';
import { useConstraintStore } from './constraintStore';
import { resolveMarkerPoint } from './attach';
import {
    COINCIDENT_EPSILON,
    type ConstraintRef,
    type AutoConstrainSettings,
} from './types';
import type { ConstraintInput } from './constraintStore';
import type { ConstraintKind } from './kinds';

interface DraftSegment {
    entity: Entity;
    a: Pt3;
    b: Pt3;
    /** Writable markers matching a and b (line: 0/1; polyline: vertex ids). */
    markerA: number;
    markerB: number;
    /** Segment belongs to an arc citizen (skip endpoint Coincident). */
    radial?: boolean;
}

interface Radial {
    entity: Entity;
    center: Pt3;
    radius: number;
}

const DEG = 180 / Math.PI;

/** Straight segment extraction (bulge segments filtered, OCS parity). */
function segmentsOf(entity: Entity): DraftSegment[] {
    const d = entity.data as Record<string, any>;
    switch (entity.type) {
        case 'line': {
            const a = resolveMarkerPoint(entity, 0), b = resolveMarkerPoint(entity, 1);
            if (!a || !b) return [];
            return [{ entity, a, b, markerA: 0, markerB: 1 }];
        }
        case 'polyline':
        case 'polygon': {
            const vs = (Array.isArray(d.vertices) ? d.vertices : []) as Pt3[];
            if (vs.length < 2) return [];
            const segs: DraftSegment[] = [];
            const n = d.closed ? vs.length : vs.length - 1;
            const bulges = Array.isArray(d.bulges) ? (d.bulges as number[]) : [];
            for (let i = 0; i < n; i++) {
                // OCS: bulged (arc) segments are excluded from inference.
                if (Math.abs(bulges[i] ?? 0) > 1e-9) continue;
                const a = [vs[i][0], vs[i][1], vs[i][2] ?? 0] as Pt3;
                const bRaw = vs[(i + 1) % vs.length];
                const b = [bRaw[0], bRaw[1], bRaw[2] ?? 0] as Pt3;
                segs.push({ entity, a, b, markerA: i, markerB: (i + 1) % vs.length });
            }
            return segs;
        }
        case 'arc': {
            // Tangent/Concentric participate; endpoint Coincident does not
            // (arc endpoints derive from center+radius+sweep here).
            const start = resolveMarkerPoint(entity, 0), end = resolveMarkerPoint(entity, 1);
            if (!start || !end) return [];
            return [{ entity, a: start, b: end, markerA: 0, markerB: 1, radial: true }];
        }
        default:
            return [];
    }
}

function radialOf(entity: Entity): Radial | null {
    if (entity.type !== 'circle' && entity.type !== 'arc') return null;
    const c = resolveMarkerPoint(entity, -3);
    const rp = resolveMarkerPoint(entity, -7);
    if (!c || !rp) return null;
    return { entity, center: c, radius: Math.hypot(rp[0] - c[0], rp[1] - c[1]) };
}

/** Segment direction angle normalized to [0, 180). */
function angleOfDeg(s: DraftSegment): number {
    const deg = Math.atan2(s.b[1] - s.a[1], s.b[0] - s.a[0]) * DEG;
    return ((deg % 180) + 180) % 180;
}

/** Segment-vs-segment intersection test (proper crossing or touch). */
function segmentsIntersect(s1: DraftSegment, s2: DraftSegment): boolean {
    const d1 = { x: s1.b[0] - s1.a[0], y: s1.b[1] - s1.a[1] };
    const d2 = { x: s2.b[0] - s2.a[0], y: s2.b[1] - s2.a[1] };
    const denom = d1.x * d2.y - d1.y * d2.x;
    if (Math.abs(denom) < 1e-12) return false;
    const t = ((s2.a[0] - s1.a[0]) * d2.y - (s2.a[1] - s1.a[1]) * d2.x) / denom;
    const u = ((s2.a[0] - s1.a[0]) * d1.y - (s2.a[1] - s1.a[1]) * d1.x) / denom;
    return t >= -1e-9 && t <= 1 + 1e-9 && u >= -1e-9 && u <= 1 + 1e-9;
}

/** Distance from point p to the segment's infinite line + foot parameter. */
function pointToLine(p: [number, number], s: DraftSegment): { dist: number; t: number } {
    const abx = s.b[0] - s.a[0], aby = s.b[1] - s.a[1];
    const len2 = abx * abx + aby * aby || 1;
    const t = ((p[0] - s.a[0]) * abx + (p[1] - s.a[1]) * aby) / len2;
    const footX = s.a[0] + t * abx, footY = s.a[1] + t * aby;
    return { dist: Math.hypot(p[0] - footX, p[1] - footY), t };
}

/** True when the pair of refs already exists as a recorded constraint. */
function duplicateExists(kind: string, refs: ConstraintRef[]): boolean {
    const key = (list: ConstraintRef[]) =>
        list.map((r) => `${r.entityId}:${r.marker}`).sort().join('|');
    const target = key(refs);
    const { set } = useConstraintStore.getState();
    return set.constraints.some((c) => c.kind === kind && key(c.refs) === target);
}

const ref = (entityId: string, marker: number): ConstraintRef => ({ entityId, marker });

/**
 * Run AUTOCONSTRAIN over the given entities (empty selection = the whole
 * model space). Returns the proposed inputs for the caller to commit as
 * ONE undoable constraint step.
 */
export function inferAutoConstrain(
    entityIds: string[],
    settings: AutoConstrainSettings
): { inputs: ConstraintInput[]; skipped: number; scanned: number } {
    const entities = useAppStore.getState().entities;
    const pool = (entityIds.length > 0 ? entityIds : Object.keys(entities))
        .map((id) => entities[id])
        .filter((e): e is Entity => !!e && ['line', 'polyline', 'polygon', 'circle', 'arc'].includes(e.type));

    const segments = pool.flatMap(segmentsOf);
    const radials = pool.map(radialOf).filter((r): r is Radial => !!r);
    const angleTol = settings.angleToleranceDeg;
    const distTol = settings.distanceTolerance;

    const inputs: ConstraintInput[] = [];
    let skipped = 0;
    const push = (kind: ConstraintKind, refs: ConstraintRef[], value?: number): void => {
        if (duplicateExists(kind, refs)) { skipped++; return; }
        inputs.push({ kind, refs, value, enabled: true });
    };

    // --- Segment vs segment ---
    for (let i = 0; i < segments.length; i++) {
        for (let j = i + 1; j < segments.length; j++) {
            const s1 = segments[i], s2 = segments[j];
            if (s1.entity.id === s2.entity.id) continue;
            const free1 = !s1.radial, free2 = !s2.radial;

            // Coincident: OCS dedicated tight epsilon (1e-6), not the
            // generic distance tolerance (Faz 6.6 scenario 6 contract).
            let bestDist = Infinity;
            let bestA = 0, bestB = 0;
            for (const [ma, pa] of [[s1.markerA, s1.a], [s1.markerB, s1.b]] as Array<[number, Pt3]>) {
                for (const [mb, pb] of [[s2.markerA, s2.a], [s2.markerB, s2.b]] as Array<[number, Pt3]>) {
                    const dist = Math.hypot(pa[0] - pb[0], pa[1] - pb[1]);
                    if (dist < bestDist) { bestDist = dist; bestA = ma; bestB = mb; }
                }
            }
            if (free1 && free2 && bestDist <= COINCIDENT_EPSILON) {
                push('Coincident', [ref(s1.entity.id, bestA), ref(s2.entity.id, bestB)]);
                continue;
            }

            const ang1 = angleOfDeg(s1), ang2 = angleOfDeg(s2);
            const angDiff = Math.min(Math.abs(ang1 - ang2), 180 - Math.abs(ang1 - ang2));

            if (angDiff < angleTol) {
                const onLine = Math.min(
                    pointToLine([s2.a[0], s2.a[1]], s1).dist,
                    pointToLine([s2.b[0], s2.b[1]], s1).dist
                );
                // Colinear outranks Horizontal/Vertical/Parallel.
                if (onLine <= distTol && free1 && free2) {
                    push('Colinear', [
                        ref(s1.entity.id, s1.markerA), ref(s1.entity.id, s1.markerB),
                        ref(s2.entity.id, s2.markerA), ref(s2.entity.id, s2.markerB),
                    ]);
                    continue;
                }
                // Axis-aligned segments become H/V on both, never Parallel.
                const isH1 = ang1 < angleTol || ang1 > 180 - angleTol;
                const isV1 = Math.abs(ang1 - 90) < angleTol;
                if ((isH1 || isV1) && free1 && free2) {
                    const kind: ConstraintKind = isH1 ? 'Horizontal' : 'Vertical';
                    push(kind, [ref(s1.entity.id, s1.markerA), ref(s1.entity.id, s1.markerB)]);
                    push(kind, [ref(s2.entity.id, s2.markerA), ref(s2.entity.id, s2.markerB)]);
                    continue;
                }
                if (free1 && free2) {
                    push('Parallel', [
                        ref(s1.entity.id, s1.markerA), ref(s1.entity.id, s1.markerB),
                        ref(s2.entity.id, s2.markerA), ref(s2.entity.id, s2.markerB),
                    ]);
                }
                continue;
            }

            // Perpendicular: near-90 angle; OCS setting may require a real
            // intersection of the two segments.
            if (Math.abs(angDiff - 90) < angleTol && free1 && free2) {
                if (!settings.perpendicularMustIntersect || segmentsIntersect(s1, s2)) {
                    push('Perpendicular', [
                        ref(s1.entity.id, s1.markerA), ref(s1.entity.id, s1.markerB),
                        ref(s2.entity.id, s2.markerA), ref(s2.entity.id, s2.markerB),
                    ]);
                    continue;
                }
            }

            // Equal lengths (optional, OCS default off).
            if (settings.equalEnabled && free1 && free2) {
                const l1 = Math.hypot(s1.b[0] - s1.a[0], s1.b[1] - s1.a[1]);
                const l2 = Math.hypot(s2.b[0] - s2.a[0], s2.b[1] - s2.a[1]);
                if (Math.abs(l1 - l2) <= distTol) {
                    push('Equal', [
                        ref(s1.entity.id, s1.markerA), ref(s1.entity.id, s1.markerB),
                        ref(s2.entity.id, s2.markerA), ref(s2.entity.id, s2.markerB),
                    ], 0);
                }
            }
        }
    }

    // --- Radial (circle/arc) vs radial ---
    for (let i = 0; i < radials.length; i++) {
        for (let j = i + 1; j < radials.length; j++) {
            const r1 = radials[i], r2 = radials[j];
            if (r1.entity.id === r2.entity.id) continue;
            const d = Math.hypot(r1.center[0] - r2.center[0], r1.center[1] - r2.center[1]);
            if (d <= COINCIDENT_EPSILON) {
                push('Concentric', [ref(r1.entity.id, -3), ref(r2.entity.id, -3)]);
                continue;
            }
            const outErr = Math.abs(d - (r1.radius + r2.radius));
            const inErr = Math.abs(d - Math.abs(r1.radius - r2.radius));
            if (Math.min(outErr, inErr) <= distTol) {
                push('Tangent', [ref(r1.entity.id, -3), ref(r1.entity.id, -7), ref(r2.entity.id, -3), ref(r2.entity.id, -7)], -1);
                continue;
            }
            if (settings.equalEnabled && Math.abs(r1.radius - r2.radius) <= distTol) {
                push('Equal', [ref(r1.entity.id, -3), ref(r1.entity.id, -7), ref(r2.entity.id, -3), ref(r2.entity.id, -7)], -1);
            }
        }
    }

    // --- Radial vs segment: tangency ---
    for (const rad of radials) {
        for (const seg of segments) {
            if (rad.entity.id === seg.entity.id || seg.radial) continue;
            const { dist, t } = pointToLine([rad.center[0], rad.center[1]], seg);
            if (Math.abs(dist - rad.radius) > distTol) continue;
            if (settings.tangentMustSharePoint && (t < 0 || t > 1)) continue;
            push('Tangent', [
                ref(seg.entity.id, seg.markerA),
                ref(seg.entity.id, seg.markerB),
                ref(rad.entity.id, -3),
                ref(rad.entity.id, -7),
            ], 0);
        }
    }

    return { inputs, skipped, scanned: pool.length };
}
